<script lang="ts">
    import { type ActivityAxisType, type XAxisData, type SharedActivityMarked, type SharedActivityRecord, type MapControllerInterface, noopMapController, type AfterChartZoom, defaultMapControllerInterface, type PowerType } from "@/Common/constants";
    import { getContext, onMount, tick } from "svelte";
    import { getXertAm5, type am5Root, type am5xyChart, powerGradient, type SectionController, createChartController, type ChartController, createEmptyChartController, } from "../Util/XertAm5";
    import { LineSeries } from "@amcharts/amcharts5/xy";
    import type { MyFitnessStores } from "@/MyFitness/stores";
    import { Legend, type Tooltip } from "@amcharts/amcharts5";
    import Button from "@/lib/components/ui/button/button.svelte";
    import * as RadioButtonGroup from "$lib/components/ui/radio-button-group/index.js";
    import * as bounds from 'binary-search-bounds'
    import { writable } from "svelte/store";
    import { cn } from "@/lib/utils";
    import ScrollArea from "@/lib/components/ui/scroll-area/scroll-area.svelte";

    export let sharedActivities: Record<string, SharedActivityMarked>
    export let startingAxisType: ActivityAxisType = "time"
    
    export let map: MapControllerInterface = {
        controller: noopMapController,
        group: null
    }
    map = {...defaultMapControllerInterface, ...map}
    // let className: string = undefined
    // export {className as class}
    export let afterZoom: AfterChartZoom = () => {}

    // For bind
    export let chartController: ChartController = createEmptyChartController()
    const myFitness = getContext<MyFitnessStores>('myFitness')
    const { userParams } = myFitness

    const{ am5, am5xy, MyTheme, createCursor, createMyDateAxis, createPowerAxis, createSpeedAxis, fastGroupData, createAltitudeAxisSeries, createSectionController, hideSeriesAxisWhenDeselected, chartReady } = getXertAm5()
    am5.addLicense('AM5C87942260');
        
    let chartElement: HTMLElement
    let legendContainer: HTMLElement
    let root: am5Root
    let chart: am5xyChart
    let axisType = writable<ActivityAxisType>(startingAxisType)
    let powerType: PowerType = "watts"
    let sectionController: SectionController = null

    // Data from activity owner, used for zooming and indexing
    let data: SharedActivityRecord[]
    let dataDist: SharedActivityRecord[]
    let mainDataSeries: LineSeries

    let activitySeriesInfo: Record<string, {
        color: string
        power: LineSeries,
        mpa: LineSeries,
        speed: LineSeries,
        relativePower: LineSeries,
        hidden: boolean,
        legend: Legend
    }> = {}

    let switchingAxisType = false
    let validateData: (axisType: ActivityAxisType) => Promise<void>
	let switchAxisType: (axisType: ActivityAxisType) => void
    let addUser: (path: string, activity: SharedActivityMarked) => void
    function getChartData(axisType: ActivityAxisType) {
        return axisType === "time" ? data : dataDist
    }

    // If sharedActivities is changed revalidate the data.
    const revalidateData = async () => { 
        if (!validateData) return

        // Remove activities that are no longer present
        for (const path of Object.keys(activitySeriesInfo)) {
            if (!(path in sharedActivities)) {
                let series = activitySeriesInfo[path];
                series.power.dispose();
                series.mpa.dispose();
                series.speed.dispose();
                series.relativePower.dispose();
                series.legend.dispose();
                series.hidden = true;
                map.controller.hideMarker(path, map.group);
                delete activitySeriesInfo[path];
            }
        }

        // Add new activities that are not present in activitySeriesInfo
        for (const [path, activity] of Object.entries(sharedActivities)) {
            if (!(path in activitySeriesInfo)) {
                await tick();
                addUser(path, activity);
                // Use Object.defineProperty to avoid triggering Svelte reactivity
                Object.defineProperty(sharedActivities[path], "dirty", { value: true, writable: true, configurable: true, enumerable: true });
            }
        }

        validateData($axisType)
        chartController.switchPowerType(powerType); // Ensures the correct series are shown.
    }
    $: if(sharedActivities) revalidateData()
    

    onMount(() => {
        // TODO: Integrate playback functionality
        root = am5.Root.new(chartElement);
        root.setThemes([MyTheme.new(root)]);

        chart = root.container.children.push(am5xy.XYChart.new(root, {
            paddingLeft: 0,
            paddingTop: -8,
            layout: root.verticalLayout,
        }) );

        chart.topPlotContainer.children.removeValue(chart.zoomOutButton);
        let buttonsContainer = chart.topPlotContainer.children.push(am5.Container.new(root, {
            layout: root.horizontalLayout,
            width: am5.p100,
            paddingRight: 10,
            //x: am5.p100
        }));

        buttonsContainer.children.push(am5.Container.new(root, {
            width: am5.p100,
        }));
        let zoomOutButton = buttonsContainer.children.push(chart.zoomOutButton);
        // Hack because am5 has a bug where zoomOut doesn't hide the button like it should
        //@ts-expect-error
        chart._handleZoomOut = ()=>{
            // Only compare x-axis for zoom button.
            if ((dateAxis.get('start') == 0) && (dateAxis.get('end') == 1)) {
                zoomOutButton.hide();
            } else {
                zoomOutButton.show();
            }
        };

        const {
            dateAxis,
            toggleX,
            setMainDataSeries,
            setValidateData,
            createScrollbar
        } = createMyDateAxis({
            root,
            chart,
            startingAxisType,
            distanceUnits: $userParams.activityUnits['dist'],
            handleRangeChange: (selectedMin, selectedMax, _axisType) => {
                map.controller.setCenterFromData(selectedMin, selectedMax, getChartData(_axisType));
                afterZoom(selectedMin, selectedMax, getChartData(_axisType), _axisType);
            },
        });
        const {sbDateAxis, sbFillSeries} = createScrollbar({root, chart})

        const powerAxis = createPowerAxis({root, chart})
        const speedAxis = createSpeedAxis({root, chart, unit: $userParams.activityUnits['spd']})
        const {altitudeSeries, altitudeAxis} = createAltitudeAxisSeries({root, chart, dateAxis, startingAxisType, unit: $userParams.activityUnits["alt"]})
        
        const cursor = createCursor({root, chart, 
            handleChange: (pos) => {
                let position = dateAxis.toAxisPosition(pos); // TODO: Pretty sure that this just returns the same number as pos  
                const dataContext = dateAxis.getSeriesItem(mainDataSeries, position)?.dataContext
                if(!dataContext) return;
                
                // Match group ride data by time, even if distance axis used.
                // This way relative position of riders is preserved on the map.
                // TODO: The playback requires setting BoundsHeadingTilt, see mpa.js
                for(const [path, activity] of Object.entries(sharedActivities)) {

                    const arg = {unix_time: dataContext.unix_time + 500};
                    const cmp = (a, b) => {return a.unix_time - b.unix_time; };
                    const index = bounds.le(activity.records, arg, cmp);
                    const data = index >= 0 ? activity.records[index] : null;

                    if (data && dataContext.unix_time - data.unix_time <= 1500 && !activitySeriesInfo[path].hidden && data.lat) {
                        let data = activity.records[index];
                        map.controller.showMarker(path, map.group);
                        map.controller.setMarkerPosition(path, data.lat, data.lng, map.group);
                    } else {
                        map.controller.hideMarker(path, map.group);
                    }
                }
        }})

        addUser = function(path: string, activity: SharedActivityMarked) {
            const user = activity.user
            let userColor = chart.get('colors').next().toCSSHex()

            let powerTooltip: Tooltip = null
            let relativePowerTooltip: Tooltip = null
            let mpaTooltip: Tooltip = null
            let speedTooltip: Tooltip = null
            if(user.me) {   
                // Use current user's series to show a combined tooltip to save space.
                powerTooltip = am5.Tooltip.new(root, {
                    //labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    labelText: 'Power:',
                });
                const fill = am5.color(0xF9F9F9);
                const stroke = am5.color(0);
                powerTooltip.get('background').set('fill', fill);
                powerTooltip.get('background').set('stroke', stroke);

                relativePowerTooltip = am5.Tooltip.new(root, {
                    //labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    labelText: 'Relative Power:',
                });
                relativePowerTooltip.get('background').set('fill', fill);
                relativePowerTooltip.get('background').set('stroke', stroke);

                // TODO: I think this always returns null. Not sure why it's here ??
                const context = root._renderer._ghostContext;
                console.log(context)
                const labelFont = '14px '+powerTooltip.label.get('fontFamily');
                let textWidth = txt => {
                    if (!context) return 0.0;
                    const font = context.font;
                    context.font = labelFont;
                    const metrics = context.measureText(txt);
                    context.font = font;
                    return metrics.width;
                }
                const padding_base = textWidth(' 1888.8');

                const getTooltipAdapter = (seriesField: "mpa" | "power" | "speed" | "relativePower", format = null, sortFunc = null) => {
                    if (!format) format = ()=>'';
                    return (text, target) => {
                        let sortItems = [];
                        const item = target.dataItem;
                        if (!item) return text;
                        for (const [path, info] of Object.entries(activitySeriesInfo)) {
                            const series = info[seriesField];
                            if (!series.isVisible()) continue;
                            const data = dateAxis.getSeriesItemByValue(series, item.get('valueX'));
                            const activity = sharedActivities[path];

                            if (data) {
                                const valueY = data.get('valueY') ?? 0;
                                let line = `\n[${series.get('stroke').toString()}]●[/] [width:100]${activity.user.display_name}:[/]${format(activity.user, item, data, valueY)}`

                                sortItems.push({
                                    valueY: valueY,
                                    data: data,
                                    line: line
                                })
                            }
                        }
                        if (sortFunc)
                            sortItems.sort(sortFunc);
                        for (const { line } of sortItems) {
                            text += line;
                        }
                        return text;
                    }
                };

                const getColor = (sig, item, p) => {
                    const v = item.dataContext;
                    const mpa = v['mmp0s'] ?? v['mpa']; // TODO: Pick whether to use mpa vs mmp names.
                    const mmp = v['mmpMid'];
                    const ftp = sig.ftp;
                    const ltp = sig.ltp ?? (sig.ftp - sig.atc/400);
                    const ltpMid = (ltp + ftp) /2;

                    //let c = am5.Color.fromString(v["powerColor"]);
                    const powers = [0, ltp, ltpMid, ftp, mmp, mpa];
                    try {
                        return powerGradient.domain(powers)(p).darken().toString();
                    } catch (e) {
                        console.error('Bad power gradient; p: '+p+'; ps: '+powers);
                        console.log(v);
                        return '#FFF';
                    }
                };

                powerTooltip.label.adapters.add('text', getTooltipAdapter('power', (user, myData, data, valueY)=>{
                    const valueText = valueY.toFixed(0).toString();
                    const padding = padding_base - textWidth(valueText);
                    const color = getColor(user.sig, data, valueY);
                    return `[width: ${padding}] [/][${color} bold]${valueText} W[/]`;
                }));

                relativePowerTooltip.label.adapters.add('text', getTooltipAdapter('relativePower', (user, myData, data, valueY)=>{
                    const valueText = valueY.toFixed(0).toString();
                    const padding = padding_base - textWidth(valueText);
                    const color = getColor(user.sig, data, valueY);
                    return `[width: ${padding}] [/][${color} bold]${valueText} W[/]`;
                }));

                const display = chart.plotContainer._display;

                let getCalculateBounds = (calcBounds) => {
                    return (event) => {
                        const {oldDataItem, newDataItem, target} = event;
                        // const point = newDataItem ? newDataItem.get('point') : null;
                        // if (!point || point.x == null) {
                        //     target.remove('bounds');
                        //     return;
                        // }

                        const plotBounds = chart.plotContainer.bounds();
                        const globalBottomRight = display.toGlobal({x: plotBounds.right, y: plotBounds.bottom });
                        const globalTopLeft = display.toGlobal({x: 0, y: 0 });

                        const h = target.height();
                        const w = target.width();
                        if (w == 0) return;

                        // TODO: Do we need to get positionX ??
                        // const positionX = cursor.get('positionX');
                        // const cursorX = positionX ? cursor._getPoint(positionX).x : cursor.getPrivate('point').x;
                        const cursorX = cursor.getPrivate('point').x;
                        const globalCursor = display.toGlobal({x: cursorX, y: 0 });

                        let left: number, right: number;
                        if (cursorX > chart.plotContainer.width() / 2 ) {
                            right = globalCursor.x - 8;
                            left = right - w;
                        } else {
                            left = globalCursor.x + 8;
                            right = left + w;
                        }


                        // const bottom = 500;
                        target.set('bounds', calcBounds(w, h, left, right, globalTopLeft, globalBottomRight));
                    }

                }

                powerTooltip.events.on('dataitemchanged', getCalculateBounds(
                    (w, h, left, right, globalTopLeft, globalBottomRight)=>{
                        const bottom = globalBottomRight.y - 8;
                        return {
                            left: left,
                            right: right,
                            bottom: bottom,
                            top: bottom - h
                        }
                    })
                );

                mpaTooltip = am5.Tooltip.new(root, {
                    //labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    labelText: 'MPA:',
                });
                mpaTooltip.get('background').set('fill', fill);
                mpaTooltip.get('background').set('stroke', stroke);
                mpaTooltip.label.adapters.add('text', getTooltipAdapter('mpa', (user, myData, data, valueY)=>{
                    const valueText = valueY.toFixed(0).toString();
                    const padding = padding_base - textWidth(valueText);

                    return `[width: ${padding}] [/][bold]${valueText} W[/]`;
                }));

                mpaTooltip.events.on('dataitemchanged', getCalculateBounds(
                    (w, h, left, right, globalTopLeft, globalBottomRight)=>{
                        const top = globalTopLeft.y + 8;
                        return {
                            left: left,
                            right: right,
                            bottom: top + h,
                            top: top
                        }
                    })
                );

                speedTooltip = am5.Tooltip.new(root, {
                    //labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    labelText: '[bold width: 154]Athlete[/][bold]Gap[/][width: 20] [/][bold]Speed[/]',
                });
                speedTooltip.get('background').set('fill', fill);
                speedTooltip.get('background').set('stroke', stroke);

                const gapUnit = ' ' + $userParams.activityUnits["alt"];

                speedTooltip.label.adapters.add('text', getTooltipAdapter('speed', (user, myData, data, valueY)=>{
                    let gapVal;
                    let gapString;
                    const mm = data.dataContext.mm;
                    if (user.me) {
                        gapVal = 0;
                        gapString = '--';
                    } else if (!mm) {
                        gapString = '--';
                        gapVal = -1E50;
                    } else if ($axisType == "time") {
                        // Show distance gap when time axis selected.
                        // gapVal = proj_dist ?? 0;
                        gapVal = mm - myData.dataContext.mm;
                        gapString = (gapVal*($userParams.metric ? 1.0/1000.0 : 5280/1000000.0)).toFixed(1) + gapUnit;
                    } else {
                        // Show time gap when distance axis selected.
                        gapVal = myData.dataContext.unix_time - data.dataContext.unix_time;
                        // gapString = formatTime(gapVal);
                        gapString = (gapVal/1000).toFixed(0)+' s';
                    }
                    data.gap = gapVal;
                    const padding_1 = 60 - textWidth(gapString);
                    const speed = valueY.toFixed(1).toString();
                    const padding_2= 60 - textWidth(speed);

                    return `[width: ${padding_1}] [/][bold]${gapString}[/][width: ${padding_2}] [/][bold]${speed}[/]`;
                }, (a, b) => b.data.gap - a.data.gap));

                speedTooltip.events.on('dataitemchanged', getCalculateBounds(
                    (w, h, left, right, globalTopLeft, globalBottomRight)=>{
                        // const top = globalTopLeft.y + 8;
                        const top = (globalTopLeft.y + globalBottomRight.y)/2;
                        return {
                            left: left,
                            right: right,
                            bottom: top + h/2,
                            top: top - h/2,
                        }
                    })
                );
            }

            const valueXField = startingAxisType === "time" ? "unix_time" : "mm"
            const powerSeries = chart.series.push(am5xy.LineSeries.new(root, {
                xAxis: dateAxis,
                yAxis: powerAxis,
                valueYField: "power",
                valueXField,
                name: user.display_name+'\'s Power',
                fill: am5.color(userColor), // White fill for dark tooltip text.
                stroke: am5.color(userColor),
                tooltip: powerTooltip,
            }));
            const relativePowerSeries = chart.series.push(am5xy.LineSeries.new(root, {
                xAxis: dateAxis,
                yAxis: powerAxis,
                valueYField: "rp",
                valueXField,
                name: user.display_name+'\'s Relative Power',
                fill: am5.color(userColor), // White fill for dark tooltip text.
                stroke: am5.color(userColor),
                tooltip: relativePowerTooltip,
            }));
            powerSeries.strokes.template.setAll({strokeWidth: 2});
            relativePowerSeries.strokes.template.setAll({strokeWidth: 2});
            if(user.me) {
                mainDataSeries = powerSeries
                setMainDataSeries(powerSeries)
                powerSeries.fills.template.setAll({visible: true, fillOpacity: 0.1})
                relativePowerSeries.fills.template.setAll({visible: true, fillOpacity: 0.1})
            }

            const mpaSeries = chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
                xAxis: dateAxis,
                yAxis: powerAxis,
                valueYField: 'mpa',
                valueXField,      
                name: user.display_name+'\'s MPA',
                fill: am5.color(0xFFFFFF), // White fill for dark tooltip text.
                stroke: am5.color(userColor),
                tooltip: mpaTooltip,
            }));
            mpaSeries.strokes.template.setAll({strokeWidth: 2, strokeDasharray:[10,5]});

            const speedSeries = chart.series.push(am5xy.LineSeries.new(root, {
                xAxis: dateAxis,
                yAxis: speedAxis,
                valueYField: 'spd',
                valueXField,  
                name: user.display_name+'\'s Speed',
                fill: am5.color(0xFFFFFF), // White fill for dark tooltip text.
                stroke: am5.color(userColor),
                tooltip: speedTooltip,
            }));
            speedSeries.strokes.template.setAll({strokeWidth: 1, strokeDasharray:[4,3]});

            // TODO: Add speedAtGrade back
            // const m_ref = 75+ 9; // +9 kg bike
            // const p_ref = 350;
            // const G = 9.81;
            // //const C_D = 0.2128; // 0.5*rho*Cd*A
            // const C_D = 0.5*0.388*1.224; // 0.5*rho*Cd*A
            // //const C_rr = 0.0008241; // Rolling resistance.
            // const C_rr = 0.005; // Rolling resistance.
            // // 0 = C_D * v^3 + C_rr*m*v^2 + m*g*sin(grade)*v - power;

            // // Cubic formula solution for 1 real root.
            // const A = C_D;
            // //const B = C_rr*m_ref;
            // const B = 0;

            // const speedAtGrade = (power, m, sin_grade)=>{
            //     const C = m*G*(sin_grade + C_rr);
            //     const D = -power;

            //     // Transform to depressed cubic.
            //     // const P = (3*A*C-B*B)/(3*A*A);
            //     // const Q = (2*B*B*B - 9*A*B*C + 27*A*A*D)/(27*A*A*A);
            //     // const R = Math.sqrt(Q*Q/4+P*P*P/27);
            //     // const t = Math.cbrt(-Q/2+R) + Math.cbrt(-Q/2-R);
            //     // // Transform back from depressed cubic.
            //     // return t - B/(3*A);

            //     // With B = 0, already a depressed cubic.
            //     const P = C/A;
            //     const Q = D/A;

            //     const R = Math.sqrt(Q*Q/4+P*P*P/27);
            //     if (Number.isNaN(R)) {
            //         // Trigonometric solution for 3 real roots.

            //         // k = 0, 1, 2 for the 3 real solutions. k = 0 seems to be the positive solution, the other 2 roots are negative.
            //         // const k = 0;
            //         // const t = 2*Math.sqrt(-P/3)*Math.cos(Math.acos(3*Q*Math.sqrt(-3.0/P)/(2*P))/3 - 2*Math.PI*k/3);
            //         return 2*Math.sqrt(-P/3)*Math.cos(Math.acos(3*Q*Math.sqrt(-3.0/P)/(2*P))/3);

            //     } else return Math.cbrt(-Q/2+R) + Math.cbrt(-Q/2-R); // Cardano formula for 1 real root.
                
            // }

            // const bike_weight = 9;
            // const m = +user.weight + bike_weight;
            // const my_weight = +$('#weight').text() + bike_weight;
            // let dist_prev = -1;
            // let alt_prev = -1;
            // if (Xert.Activity.Analytics.MPA._enable_grade_adjusted_power && !user.me) for (let i = 0; i < activity.records.length; ++i) {
            //     const dist = activity.records[i].mm;
            //     const alt = activity.records[i].alt;

            //     const ddist = dist - dist_prev;
            //     const slope = i > 0 && ddist > 1000 ? 1000*(alt - alt_prev)/(dist - dist_prev) : 0.0;
            //     const grade = slope*100;

            //     // Approximately just equal to slope
            //     const sin_theta = Math.sin(Math.atan(slope));
            //     //const sin_theta = slope;

            //     dist_prev = dist;
            //     alt_prev = alt;

            //     // const theta = Math.atan(grade/100);
            //     // const sin_theta = Math.sin(theta);
            //     // TODO: Move to webassembly?
            //     if (activity.records[i].p_orig == null) {
            //         activity.records[i].p_orig = activity.records[i].power;
            //         activity.records[i].mpa_orig = activity.records[i].mpa;
            //     }
            //     let v = speedAtGrade(activity.records[i].p_orig, m, sin_theta);
            //     const adjusted = v*(C_D*v*v + my_weight*G*(sin_theta+C_rr));
            //     activity.records[i].power = adjusted;
            //     console.log(`m: ${m} -> ${my_weight}; power: ${activity.records[i].p_orig} -> ${adjusted}; grade: ${grade}`);

            //     v = speedAtGrade(activity.records[i].mpa_orig, m, sin_theta);
            //     activity.records[i].mpa = v*(C_D*v*v + m_ref*G*(sin_theta+C_rr));
            // }

            const legendRoot = am5.Root.new(`${path}-legend`)
            legendRoot.setThemes([am5xy.DefaultTheme.new(legendRoot)])
            let legend = legendRoot.container.children.push( 
                am5.Legend.new(legendRoot, {
                    layout: legendRoot.verticalLayout,
                })
            )
            legend.labels.template.set("forceHidden", true);
            legend.valueLabels.template.set("forceHidden", true);
            legend.markers.template.setAll({
                width: 32,
                height: 32
            });
            legend.itemContainers.template.setAll({
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
            })
            if (powerType === "watts") {
                legend.data.setAll([mpaSeries, powerSeries, speedSeries]);
            } else if (powerType === "relative") {
                legend.data.setAll([relativePowerSeries, speedSeries]);
            }
            

            activitySeriesInfo[path] = {
                color: userColor,
                power: powerSeries,
                mpa: mpaSeries,
                speed: speedSeries,
                relativePower: relativePowerSeries,
                hidden: false,
                legend,
            }

            const firstGPS = activity.records.find(r => !isNaN(r.lat));
            map.controller.addMarker({
                id: path,
                image: user.avatar,
                lat: firstGPS.lat,
                lng: firstGPS.lng,
                label: user.display_name,
                borderColor: userColor,
                size: {
                    width: 20,
                    height: 20,
                },
                group: map.group,
            })

            let series = [powerSeries, mpaSeries, speedSeries, relativePowerSeries]
            series.forEach((series) => {
                series.on("visible", () => {
                    if ((!powerSeries.get("visible")) && !mpaSeries.get("visible") && !speedSeries.get("visible") && !relativePowerSeries.get("visible")) {
                        map.controller.hideMarker(path, map.group);
                        activitySeriesInfo[path].hidden = true;
                    } else {
                        map.controller.showMarker(path, map.group);
                        activitySeriesInfo[path].hidden = false;
                    }
                });
            });
        }
        for (const [path, activity] of Object.entries(sharedActivities)) {
            addUser(path, activity);
        }

        // Legend for altitude series.
        const legendRoot = am5.Root.new(`altitude-legend`)
        legendRoot.setThemes([am5xy.DefaultTheme.new(legendRoot)])
        let legend = legendRoot.container.children.push( 
            am5.Legend.new(legendRoot, {
                layout: legendRoot.verticalLayout,
            })
        )
        // legend.labels.template.set("forceHidden", true);
        legend.valueLabels.template.set("forceHidden", true);
        legend.markers.template.setAll({
            width: 16,
            height: 16
        });
        legend.data.setAll([altitudeSeries])

        sectionController = createSectionController(dateAxis, powerAxis, data, startingAxisType, map.controller)

        validateData = async function(axis: ActivityAxisType) {
            for (const [path, activity] of Object.entries(sharedActivities)) {
                let series = (({power, mpa, speed, relativePower}) => [power, mpa, speed, relativePower])(activitySeriesInfo[path])
                if(sharedActivities[path].dirty || $axisType !== axis) {
                    if(!activity.user.me) fastGroupData(series, activity.records, axis, [dateAxis]);
                    else ({data, dataDist} = fastGroupData([...series, sbFillSeries, altitudeSeries], activity.records, axis, [dateAxis, sbDateAxis]));
                    // Use Object.defineProperty to avoid triggering Svelte reactivity
                    Object.defineProperty(sharedActivities[path], "dirty", { value: false, writable: true, configurable: true, enumerable: true });
                }
            }
            sectionController.switchAxis(axis, getChartData(axis));
        }
        validateData(startingAxisType)
        setValidateData(validateData)

        hideSeriesAxisWhenDeselected({root, chart})

        function switchPowerType(type: PowerType) {
            powerType = type
            for (const info of Object.values(activitySeriesInfo)) {
                if (powerType === "watts") {
                    info.legend.data.setAll([info.mpa, info.power, info.speed]);
                    if(!info.hidden) {
                        info.power.show();
                        info.mpa.show();
                        info.relativePower.hide();
                    }     
                } else if (powerType === "relative") {
                    info.legend.data.setAll([info.relativePower, info.speed]);
                    if(!info.hidden) {
                        info.power.hide();
                        info.mpa.hide();
                        info.relativePower.show();
                    }                    
                }
            }
        }

        // The event handler needs to be separated from the function to allow for the dom to update correctly.
        async function _switchAxisType(_axisType: ActivityAxisType) {
            if(_axisType === $axisType) return;
            switchingAxisType = true;
            $axisType = await toggleX(_axisType === "time" ? dataDist : data)
            switchingAxisType = false

        }
        switchAxisType  = function(axisType: ActivityAxisType) {
            switchingAxisType = true;
            setTimeout(() => _switchAxisType(axisType))
        }

        chartController = createChartController(sectionController, axisType, _switchAxisType, switchPowerType, chartReady(root))

        dateAxis.events.on('boundschanged', (event)=>{
            let w = dateAxis.width();
            if (w > 0) {
                dateAxis.set('groupCount', w);
                sbDateAxis.set('groupCount', w);
                legendContainer.style.width = w + "px";
            }
        });

        // Hiding series via visble propery doesn't work for us so we have to do it manually.
        for (const [path, info] of Object.entries(activitySeriesInfo)) {
            info.relativePower.hide();
            if(sharedActivities[path].user?.me) continue
            let seriesGroup = [info.speed, info.mpa, info.power];
            toggleSeriesGroupVisibility(seriesGroup);
        }
             
    })
   
    function toggleSeriesGroupVisibility(seriesGroup: LineSeries[]) {
        let makeVisible = false
        for (const series of seriesGroup) {
            if(!series.get('visible')) {
                makeVisible = true
                break
            }
        }
        seriesGroup.forEach((series) => {makeVisible ? series.show() : series.hide()})
    }

    function toggleUserSeriesVisibility(path: string) {
        const info = activitySeriesInfo[path];
        let seriesGroup = [info.speed];
        if (powerType === "watts") {
            seriesGroup.unshift(info.mpa, info.power);
        } else {
            seriesGroup.unshift(info.relativePower);
        }
        toggleSeriesGroupVisibility(seriesGroup);
    }

    function toggleSeriesTypeVisibility(type: "mpa" | "power" | "speed" | "relativePower") {
        toggleSeriesGroupVisibility(Object.values(activitySeriesInfo).map((info) => info[type]))
    }
    
</script>

<div class={cn(
    "relative",
    switchingAxisType && "pointer-events-none"
)}>
    {#if switchingAxisType}
        <div class="absolute inset-0 z-10">
            <i class="fa-solid fa-spinner animate-spin text-2xl text-muted-foreground"/>
        </div>
    {/if}

    <div class="h-96" bind:this={chartElement}/>
    <div class="ml-[3.25rem] space-y-3">
        <div bind:this={legendContainer} class="flex gap-4 border-border border rounded p-1">
            <div class="flex flex-col gap-1">
                <div class="h-[2px]"/>
                <div class={cn("grid", powerType === "relative" ? "grid-rows-3" : "grid-rows-4")}>
                    <div class="h-8"/>
                    {#if powerType !== "relative"}<Button size="sm" variant="ghost" on:click={() => toggleSeriesTypeVisibility("mpa")}>MPA</Button>{/if}
                    <Button size="sm" variant="ghost" on:click={() => toggleSeriesTypeVisibility(powerType === "watts" ? "power" : "relativePower")}>Power</Button>
                    <Button size="sm" variant="ghost" on:click={() => toggleSeriesTypeVisibility("speed")}>Speed</Button>
                    
                </div>
            </div>
            <ScrollArea orientation="horizontal">
                <div class="flex gap-2">
                    {#each Object.entries(sharedActivities) as [path, activity] (path)}
                        <div class="flex flex-col gap-1 items-center">
                            {#if !activitySeriesInfo[path]?.hidden}
                                <div class="h-[2px] w-full" style={`background: ${activitySeriesInfo[path]?.color}`}/>
                            {/if}
                            <Button size="sm" variant="ghost" class="font-medium font-sm" on:click={() => toggleUserSeriesVisibility(path)}>{activity.user.display_name}</Button>
                            <div class={cn("w-8", powerType === "relative" ? "h-16" : "h-24")} id={`${path}-legend`}/>
                        </div>
                    {/each}
                </div>
            </ScrollArea>
        </div>
        <div class="flex gap-4 items-center">
            <RadioButtonGroup.Root variant="outline" grouped value={$axisType} 
                onValueChange={switchAxisType}
            >
                <RadioButtonGroup.Button value="time">
                    Time
                </RadioButtonGroup.Button>
                <RadioButtonGroup.Button value="distance">
                    Distance
                </RadioButtonGroup.Button>
            </RadioButtonGroup.Root>
            <div class="border-border border rounded-md h-9 px-3">
                <div class="w-[85px] h-full" id={'altitude-legend'} />
            </div>
            <slot name="buttons"/>
        </div>
    </div>
</div>